import { Suspense, lazy } from 'react'
import { Routes, Route, Navigate, Link } from 'react-router-dom'
import { SessionProvider, useSession } from './auth/SessionProvider'
import { RequireRole } from './auth/RequireRole'
import { LoginPage, FirstRunSetupPage } from './auth/LoginPage'
import { RunListPage } from './runner/RunListPage'
import { RunnerPage } from './runner/RunnerPage'
import { AppShell } from './AppShell'
import { ErrorBoundary } from './ErrorBoundary'
import { ToastProvider, EmptyState, LoadingScreen } from './ui'
import type { Role } from '@shared/schema/api'

/**
 * หน้าของผู้ดูแลระบบถูกโหลดแยก (lazy)
 * พนักงานภาคสนามที่ใช้มือถือบนสัญญาณอ่อนจึงไม่ต้องโหลดโค้ดของหน้าออกแบบผัง
 * (React Flow + dnd-kit) ซึ่งเป็นก้อนใหญ่ที่สุดและเขาไม่ได้ใช้เลย
 */
const WorkflowsPage = lazy(() =>
  import('./workflows/WorkflowsPage').then((m) => ({ default: m.WorkflowsPage })),
)
const BuilderPage = lazy(() => import('./builder/BuilderPage').then((m) => ({ default: m.BuilderPage })))
const DesignerPage = lazy(() =>
  import('./designer/DesignerPage').then((m) => ({ default: m.DesignerPage })),
)
const SubmissionsPage = lazy(() =>
  import('./admin/SubmissionsPage').then((m) => ({ default: m.SubmissionsPage })),
)
const UsersPage = lazy(() => import('./admin/UsersPage').then((m) => ({ default: m.UsersPage })))
const DashboardPage = lazy(() =>
  import('./dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
)

function NotFoundPage() {
  return (
    <div className="p-5">
      <EmptyState
        title="ไม่พบหน้าที่ต้องการ"
        description="ลิงก์อาจเปลี่ยนไปแล้ว หรือพิมพ์ที่อยู่ผิด"
        action={
          <Link
            to="/"
            className="inline-flex min-h-11 items-center rounded-lg bg-[var(--fs-primary)] px-4 py-2.5 text-sm font-medium text-[var(--fs-on-primary)]"
          >
            กลับหน้าแรก
          </Link>
        }
      />
    </div>
  )
}

/** หน้าแรกของแต่ละบทบาท — พาไปหน้าเดียวที่เขาใช้งานได้จริง ไม่ใช่หน้าที่กดแล้วโดนปฏิเสธ */
function HomeRedirect() {
  const { user } = useSession()
  const home = user?.role === 'admin' ? '/workflows' : user?.role === 'report' ? '/submissions' : '/run'
  return <Navigate to={home} replace />
}

/** ห่อหน้าด้วยการตรวจสิทธิ์ + โครงหน้า + กันหน้าจอขาวเมื่อ component พัง */
function shell(element: React.ReactNode, roles?: Role[]) {
  return (
    <RequireRole roles={roles}>
      <AppShell>
        <ErrorBoundary>
          <Suspense fallback={<LoadingScreen />}>{element}</Suspense>
        </ErrorBoundary>
      </AppShell>
    </RequireRole>
  )
}

/**
 * เหมือน shell() แต่ไม่มีแถบเมนูบนสุดของแอป (โลโก้/ลิงก์นำทาง/ออกจากระบบ)
 * ใช้กับหน้าที่ต้องเต็มจอไม่มีสิ่งรบกวน เช่นตอนพนักงานกำลังสัมภาษณ์จริง
 */
function bareShell(element: React.ReactNode, roles?: Role[]) {
  return (
    <RequireRole roles={roles}>
      <ErrorBoundary>
        <Suspense fallback={<LoadingScreen />}>{element}</Suspense>
      </ErrorBoundary>
    </RequireRole>
  )
}

export function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <SessionProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/setup" element={<FirstRunSetupPage />} />

            <Route path="/" element={shell(<HomeRedirect />)} />

            {/* ผู้ดูแลระบบ */}
            <Route path="/workflows" element={shell(<WorkflowsPage />, ['admin'])} />
            <Route path="/workflows/:id" element={shell(<BuilderPage />, ['admin'])} />
            <Route
              path="/workflows/:id/design/:nodeId"
              element={shell(<DesignerPage />, ['admin'])}
            />
            <Route path="/users" element={shell(<UsersPage />, ['admin'])} />

            {/* ผลตอบกลับและภาพรวม — ผู้ดูแลกับผู้ดูรายงาน */}
            <Route path="/submissions" element={shell(<SubmissionsPage />, ['admin', 'report'])} />
            <Route path="/dashboard" element={shell(<DashboardPage />, ['admin', 'report'])} />

            {/* ผู้ตอบแบบสอบถาม */}
            <Route path="/run" element={shell(<RunListPage />, ['admin', 'field'])} />
            {/* ไม่มีแถบเมนูบนสุด — กำลังสัมภาษณ์จริงต้องเต็มจอ ไม่มีอะไรรบกวน */}
            <Route path="/run/:id" element={bareShell(<RunnerPage />, ['admin', 'field'])} />

            <Route path="*" element={shell(<NotFoundPage />)} />
          </Routes>
        </SessionProvider>
      </ToastProvider>
    </ErrorBoundary>
  )
}
